// --- Dependencies -------------------------------------------------------------------------------
import { useEffect } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
// --- Our Modules --------------------------------------------------------------------------------
import useFetch from "@/hooks/useFetch";
import usePopup from "@/hooks/usePopup";
import useTranslation from "@/hooks/useTranslation";
import useLang from "@/hooks/useLang";
import type { TyClient } from "@/types";
import { formatDate } from "@/utils";
import Avatar from "@/UI/atoms/Avatar";
import StatusChip from "@/UI/atoms/StatusChip";
import MembershipCard from "@/UI/cards/MembershipCard";
import ActionsBar, { type TyBarTab } from "@/UI/components/ActionsBar";
import FactsGrid, { type TyFact } from "@/UI/components/FactsGrid";
import RecordPanel from "@/UI/components/RecordPanel";
import ActionsPopup, { type TyAction } from "@/UI/popups/ActionsPopup";
import ClientFormPopup from "@/UI/popups/ClientFormPopup";
import ConfirmPopup from "@/UI/popups/ConfirmPopup";
import PasswordPopup from "@/UI/popups/PasswordPopup";
import "./styles.css";
// --- Logic --------------------------------------------------------------------------------------
type TyTab = "memberships" | "details";

/**
 * One person, and the answer to the question no company's own team screen can give: **which
 * companies is this one in, and what are they in each.**
 *
 * That list leads, because it is why you came. A person is one record with one phone number however
 * many companies they belong to, so when somebody rings about an account, the thing that explains it
 * is usually that they are a manager somewhere else as well.
 *
 * The id is the path segment rather than the phone number, even though the phone is the identity: a
 * number is what gets mistyped, and a near miss belongs in a search box that returns a list, not in a
 * URL that returns nothing.
 */
export default function ClientScreen() {
    const t = useTranslation();
    const lang = useLang((state) => state.lang) ?? "ar";
    const navigate = useNavigate();
    const { id = "" } = useParams();
    const addComp = usePopup((state) => state.addComp);
    const [params, setParams] = useSearchParams();

    const tab = (params.get("tab") as TyTab) || "memberships";

    const person = useFetch<{ client: TyClient }>(`/clients/${id}`, {}, false);
    const action = useFetch("/clients", {}, false);

    /** Deferred and asked for here: `useFetch` re-runs on `invoke` rather than when its path changes. */
    useEffect(() => {
        person.invoke();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    useEffect(() => {
        if (!action.succeededAt) return;
        person.invoke();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [action.succeededAt]);

    const client = person.data?.client;

    /** A password only ever exists in the response that created it, so it is shown the moment it lands. */
    useEffect(() => {
        const password = (action.data as { password?: string } | null)?.password;
        if (!action.succeededAt || !password) return;
        addComp({ name: "password", Component: PasswordPopup, anim: "scale", details: { name: client?.name ?? "", password } });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [action.succeededAt]);

    const apply = (next: Record<string, string | null>) => {
        const merged = new URLSearchParams(params);
        for (const [key, value] of Object.entries(next)) {
            if (value) merged.set(key, value);
            else merged.delete(key);
        }
        setParams(merged, { replace: true });
    };

    if (person.error) return <p className="screen-state screen-state--error">{person.error}</p>;
    // A deferred call has neither data nor `loading` on its first frame, so it reads as loading too.
    if (!client) return <p className="screen-state">{t.common.loading}</p>;

    const tabs: TyBarTab[] = [
        { key: "memberships", label: t.clients.recordTabs.memberships },
        { key: "details", label: t.clients.recordTabs.details },
    ];

    /** Only what somebody filled in — a grid of em-dashes is a full page that says nothing. */
    const facts: TyFact[] = [
        { key: "phone", label: t.clients.fields.phone, value: client.phone, ltr: true },
        { key: "email", label: t.clients.fields.email, value: client.email ?? "", ltr: true },
        { key: "status", label: t.clients.fields.status, value: client.isActive ? t.clients.active : t.clients.suspended },
        { key: "createdAt", label: t.clients.fields.createdAt, value: formatDate(client.createdAt, lang) },
        { key: "notes", label: t.clients.fields.notes, value: client.notes ?? "" },
    ].filter((fact) => fact.value);

    const openPassword = () =>
        addComp({
            name: "confirm",
            Component: ConfirmPopup,
            anim: "scale",
            details: {
                title: t.password.issue,
                message: t.password.confirmHint.replace("{name}", client.name),
                confirmLabel: t.password.issue,
                danger: false,
                onConfirm: () => action.invoke({ method: "POST", path: `/clients/${client._id}/password` }),
            },
        });

    const openActions = (anchor: DOMRect) => {
        const actions: TyAction[] = [
            {
                key: "edit",
                label: t.common.edit,
                icon: "edit",
                onSelect: () =>
                    addComp({
                        name: "client-form",
                        Component: ClientFormPopup,
                        anim: "scale",
                        details: { client, onDone: () => person.invoke() },
                    }),
            },
            {
                key: "active",
                label: client.isActive ? t.clients.suspend : t.clients.activate,
                icon: "block",
                danger: client.isActive,
                onSelect: () =>
                    addComp({
                        name: "confirm",
                        Component: ConfirmPopup,
                        anim: "scale",
                        details: {
                            title: client.isActive ? t.clients.suspend : t.clients.activate,
                            message: (client.isActive ? t.clients.confirmSuspend : t.clients.confirmActivate).replace("{name}", client.name),
                            confirmLabel: client.isActive ? t.clients.suspend : t.clients.activate,
                            danger: client.isActive,
                            onConfirm: () => action.invoke({ method: "PATCH", path: `/clients/${client._id}`, data: { isActive: !client.isActive } }),
                        },
                    }),
            },
        ];

        addComp({
            name: "client-actions",
            Component: ActionsPopup,
            place: "anchor-below",
            anim: "scaleTop",
            cover: "invisible",
            details: { actions },
            anchor: { top: anchor.top, left: anchor.left, right: anchor.right, bottom: anchor.bottom, width: anchor.width, height: anchor.height },
        });
    };

    return (
        <section className="client-screen">
            <RecordPanel
                avatar={<Avatar name={client.name} picture={client.picture} size={46} bordered />}
                title={client.name}
                subtitle={client.phone}
                chips={
                    <>
                        <StatusChip
                            size="sm"
                            tone={client.isActive ? "good" : "bad"}
                            label={client.isActive ? t.clients.active : t.clients.suspended}
                        />
                        {client.isPasswordTemporary && <StatusChip size="sm" tone="warn" label={t.password.temporary} />}
                        {client.memberships.length > 0 && (
                            <StatusChip size="sm" tone="dark" label={t.clients.inCompanies.replace("{count}", String(client.memberships.length))} />
                        )}
                    </>
                }
                primary={{ label: t.password.issue, onPress: openPassword }}
                onMore={openActions}
            >
                <ActionsBar tabs={tabs} activeTab={tab} onTab={(key) => apply({ tab: key === "memberships" ? null : key })} />
            </RecordPanel>

            {action.error && <p className="screen-state screen-state--error">{action.error}</p>}

            {/* --- The companies, which is what this screen is for ------------------------------ */}
            {tab === "memberships" && (
                <>
                    {!client.memberships.length && <p className="screen-state">{t.clients.noMemberships}</p>}

                    <div className="list-screen__rows">
                        {client.memberships.map((membership) => (
                            <MembershipCard
                                key={`${membership.organization._id}-${membership.role}`}
                                membership={membership}
                                onOpen={() => navigate(`/organizations/${membership.organization.slug}`)}
                            />
                        ))}
                    </div>
                </>
            )}

            {tab === "details" && <FactsGrid facts={facts} />}
        </section>
    );
}
